import { Box, MenuItem, TextField, Typography } from '@mui/material'
import type { ReactNode } from 'react'

export type SendConsumer = { name: string }

export type SendTargets = {
  status: 'pending' | 'error' | 'success'
  phones: string[]
  emails: string[]
}

const ContactSelect = ({
  label,
  contacts,
  helper,
}: {
  label: string
  contacts: string[]
  helper?: string
}) => (
  <TextField
    select
    size="small"
    label={label}
    value={contacts[0] ?? ''}
    disabled={contacts.length === 0}
    helperText={helper}
    sx={{ minWidth: 180, flex: 1 }}
    slotProps={{
      select: {
        MenuProps: { slotProps: { paper: { dir: 'rtl' } } },
        // bdi keeps the "+" of a number at its start inside the right to left row
        renderValue: value => <bdi>{String(value)}</bdi>,
      },
    }}
  >
    {contacts.map(contact => (
      <MenuItem key={contact} value={contact}>
        <bdi>{contact}</bdi>
      </MenuItem>
    ))}
  </TextField>
)

// Said only once the targets loaded: a send does not reach a type with none.
const noTarget = (
  targets: SendTargets,
  list: string[],
  line: string
): string | undefined =>
  targets.status === 'success' && list.length === 0 ? line : undefined

/**
 * The top row: the template dropdown, and for a send to one consumer the name
 * and the targets a send reaches. The lists only show them: the send goes to
 * every target of the consumer, so choosing one here changes nothing.
 */
const RecipientRow = ({
  consumer,
  targets,
  templateSelect,
}: {
  consumer?: SendConsumer
  targets: SendTargets
  templateSelect: ReactNode
}) => (
  <Box sx={{ mb: 2 }}>
    <Box
      data-testid={consumer ? 'recipient-row' : undefined}
      sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
    >
      {templateSelect}
      {consumer && (
        <>
          <TextField
            size="small"
            label="שם הצרכן"
            value={consumer.name}
            sx={{ minWidth: 180, flex: 1 }}
            slotProps={{
              input: { readOnly: true },
              htmlInput: { dir: 'rtl' },
            }}
          />
          <ContactSelect
            label="טלפון"
            contacts={targets.phones}
            helper={noTarget(
              targets,
              targets.phones,
              'אין טלפון יעד, ההודעה לא תגיע'
            )}
          />
          <ContactSelect
            label="מייל"
            contacts={targets.emails}
            helper={noTarget(
              targets,
              targets.emails,
              'אין מייל יעד, ההודעה לא תגיע'
            )}
          />
        </>
      )}
    </Box>
    {consumer && targets.status === 'error' && (
      <Typography variant="caption" sx={{ color: 'error.main' }}>
        לא ניתן לטעון את יעדי השליחה
      </Typography>
    )}
    {consumer && targets.status !== 'error' && (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        ההודעה נשלחת לכל יעדי השליחה של הצרכן
      </Typography>
    )}
  </Box>
)

export default RecipientRow
